"use client";

import React from "react";
import { Archive, Cable, FileText, ShieldAlert, Wind, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Storage() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const protocolChecklist = [{
    id: "spec-inflatable",
    icon: Wind,
    title: "Inflatable mattresses & surrounds",
    note: "Follow product-specific deflation and folding sequences before storing."
  }, {
    id: "spec-electronics",
    icon: Cable,
    title: "Consoles, wiring & controllers",
    note: "Shield electrical contacts and enclosures from adverse ambient exposures."
  }, {
    id: "spec-chassis",
    icon: Wrench,
    title: "Motion base & drive gear",
    note: "Inspect mounts and mechanical linkages prior to prolonged staging."
  }];
  return <section id="storeequipmentproperly" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} variants={containerVariants} className="space-y-10">
          {/* Header plate */}
          <div className="border border-border bg-card p-6 md:p-8 rounded-none shadow-xs">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-border">
              <div className="flex items-center gap-2">
                <Archive className="size-4 text-primary" />
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  MAINTENANCE &amp; STORAGE PROTOCOL
                </span>
              </div>
              <Badge variant="outline" className="font-mono text-[11px] uppercase tracking-wider rounded-none border-border px-2.5 py-0.5 bg-muted text-foreground">
                SPEC DOC: MBS-STRG-04
              </Badge>
            </div>

            <div className="pt-6 max-w-4xl">
              <h2 className="font-default text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight text-foreground uppercase">
                Store Mechanical Bull Equipment Properly
              </h2>
            </div>
          </div>

          {/* Industrial Two-Part Specification Plate */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Primary Guidance Plate */}
            <div className="border border-border bg-card p-6 md:p-8 flex flex-col justify-between rounded-none shadow-xs">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-primary font-mono text-xs uppercase font-semibold">
                  <ShieldAlert className="size-4 shrink-0" />
                  <span>Primary Storage Directive</span>
                </div>
                <p className="font-default text-base md:text-lg text-foreground leading-relaxed">
                  Storage conditions can affect mechanical components, controllers,
                  cables and inflatables. Follow the storage guidance supplied for
                  the exact equipment, especially when a machine will remain unused
                  for an extended period.
                </p>
              </div>

              <div className="pt-6 mt-6 border-t border-border flex items-center justify-between font-mono text-xs text-muted-foreground">
                <span>STAGE STATUS: EXTENDED DOWNTIME</span>
                <span className="text-foreground font-medium">REVISION 2.4</span>
              </div>
            </div>

            {/* Sub-Guidance Plate */}
            <div className="border border-border bg-card p-6 md:p-8 flex flex-col justify-between rounded-none shadow-xs">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-primary font-mono text-xs uppercase font-semibold">
                  <FileText className="size-4 shrink-0" />
                  <span>Component Specific Instructions</span>
                </div>
                <p className="font-default text-base md:text-lg text-foreground leading-relaxed">
                  Before packing an inflatable, make sure you follow its
                  product-specific deflation and storage instructions. Likewise,
                  protect controllers, cables and other components from conditions
                  the manufacturer advises against.
                </p>
              </div>

              <div className="pt-6 mt-6 border-t border-border flex items-center justify-between font-mono text-xs text-muted-foreground">
                <span>MANUFACTURER SPECS MANDATORY</span>
                <span className="text-foreground font-medium">OEM VERIFIED</span>
              </div>
            </div>
          </div>

          {/* Technical Checklist Summary Grid */}
          <div className="border border-border bg-muted/40 p-6 rounded-none">
            <div className="mb-4">
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Equipment Unit Checklist
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {protocolChecklist.map((item, i) => {
              const IconComponent = item.icon;
              return <div key={item.id} data-index={i} className="border border-border bg-card p-4 rounded-none space-y-2">
                    <div className="flex items-center gap-2 text-foreground font-medium text-sm">
                      <IconComponent className="size-4 text-primary shrink-0" />
                      <span>{item.title}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-normal">
                      {item.note}
                    </p>
                  </div>;
            })}
            </div>

            <div className="mt-6 pt-5 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="font-mono text-xs text-muted-foreground">
                Consult OEM documentation supplied with your serial number for individual machine requirements.
              </p>
              <div className="flex items-center gap-3 shrink-0">
                <Button asChild variant="outline" size="sm" className="rounded-none font-mono text-xs uppercase">
                  <Link to="/SetupSafetyAndMaintenancePage">View setup specs</Link>
                </Button>
                <Button asChild variant="default" size="sm" className="rounded-none font-mono text-xs uppercase">
                  <Link to="/ContactPage">Contact technical desk</Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}